/**
 * useFileIntake: the logic behind every drop zone and drop overlay.
 *
 * Built on react-dropzone with no `accept`, no `maxSize` and never `disabled`.
 * Its `accept` is MIME-keyed and would refuse an R script, and `disabled`
 * removes the root's drag handlers so a drop navigates the tab to the file
 * (ChatComposer's dropzone comment has the history). Judging happens here
 * instead: during a drag from the MIME types the browser exposes, and on
 * drop by name and size through `validateFiles`.
 *
 * Only a real OS file drag registers: react-dropzone activates on `Files`,
 * so a workspace item or text being dragged never changes the state.
 */

import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { useDropzone, type DropEvent, type DropzoneRootProps } from 'react-dropzone';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import { safeAnalytics } from '../../../utils/analytics';
import { countBucket } from '../../../utils/analyticsCountBucket';
import { isDropHandled } from '../../workspace/dnd/dragState';
import { nameClipboardFile } from './nameClipboardFile';
import { typeImageByName } from './rules';
import type { FileIntakeOptions, IntakeDrag, IntakeResult, IntakeSource } from './types';
import { summarizeRejections, validateFiles } from './validateFiles';

const IDLE: IntakeDrag = { state: 'idle', count: 0, acceptedCount: 0 };

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

type DragOptions = Pick<FileIntakeOptions, 'rule' | 'maxFiles' | 'readOnlyReason'>;

/** "PDF or DOCX"; "JPG, PNG or HEIC". */
const listOr = (labels: readonly string[]) =>
  labels.length > 2 ? `${labels.slice(0, -1).join(', ')} or ${labels.at(-1)}` : labels.join(' or ');

/** The drag verdict from what a drag exposes: a count and MIME types, no names. */
function classifyDrag(
  items: ReadonlyArray<{ kind: string; type: string }>,
  { rule, maxFiles, readOnlyReason }: DragOptions,
): IntakeDrag {
  const files = items.filter((item) => item.kind === 'file');
  const count = files.length;
  if (readOnlyReason) return { state: 'refuse', count, acceptedCount: 0, reason: readOnlyReason };

  const refused = files.filter((item) => rule.dragType(item.type) === 'no').length;
  const fitting = count - refused;
  const acceptedCount = Math.min(fitting, maxFiles);
  const types = listOr(rule.typeLabels);

  if (count > 0 && fitting === 0) {
    return { state: 'refuse', count, acceptedCount: 0, reason: `This place takes ${types} only.` };
  }
  if (refused > 0) {
    const verb = refused === 1 ? 'is' : 'are';
    return { state: 'partial', count, acceptedCount, reason: `${refused} ${verb} not ${types}.` };
  }
  if (fitting > maxFiles) {
    return { state: 'partial', count, acceptedCount, reason: `Up to ${maxFiles} at a time.` };
  }
  return { state: 'accept', count, acceptedCount };
}

/** The one-line headline both looks print for a drag in progress. */
export function dragHeadline(
  drag: IntakeDrag,
  destinationLabel?: string,
  readOnly = false,
): string {
  const to = destinationLabel ? ` to ${destinationLabel}` : '';
  switch (drag.state) {
    case 'idle':
      return '';
    case 'accept':
      // A browser that hides the files during a drag reports none; say "files".
      return `Release to add ${drag.count > 0 ? plural(drag.count, 'file') : 'files'}${to}`;
    case 'partial':
      return `${drag.acceptedCount} of ${plural(drag.count, 'file')} can be added`;
    case 'refuse':
      if (readOnly) return "You can't add files here";
      return drag.count === 1 ? "This file can't be added here" : "These files can't be added here";
  }
}

/** What the live region announces for a drag: the headline, then why. */
function describeDrag(drag: IntakeDrag, destinationLabel?: string, readOnly = false): string {
  const headline = dragHeadline(drag, destinationLabel, readOnly);
  if (drag.state === 'idle' || drag.state === 'accept') return headline;
  return drag.reason ? `${headline}. ${drag.reason}` : headline;
}

function sourceOf(event: DropEvent | undefined): IntakeSource {
  if (event && 'type' in event && event.type === 'drop') return 'drop';
  if (event && 'type' in event && event.type === 'paste') return 'paste';
  return 'picker';
}

function nativeEventOf(event: DropEvent): Event | null {
  if ('nativeEvent' in event) return event.nativeEvent;
  return event instanceof Event ? event : null;
}

/**
 * The toasts every intake reports with: what was refused and why, and how
 * many past the cap were left for next time. Exported for a drop target that
 * is not a zone (a folder tile), so it reports in the same words.
 */
export function useIntakeReport() {
  const toast = useAppToast();
  return useCallback(
    (
      { accepted, rejected, skipped }: IntakeResult,
      source: IntakeSource,
      { maxFiles, overLimitHint }: Pick<FileIntakeOptions, 'maxFiles' | 'overLimitHint'>,
    ) => {
      if (rejected.length > 0) {
        toast({
          title: `Couldn't add ${plural(rejected.length, 'file')}`,
          description: summarizeRejections(rejected),
          status: 'error',
          duration: 6000,
        });
      }
      if (skipped > 0) {
        const verb = source === 'drop' ? 'drop' : 'add';
        toast({
          title: `Adding the first ${accepted.length} of ${accepted.length + skipped} files`,
          description:
            overLimitHint?.(skipped) ??
            `Up to ${maxFiles} at a time, so ${verb} the other ${skipped} next.`,
          status: 'warning',
          duration: 6000,
        });
      }
    },
    [toast],
  );
}

export function useFileIntake(options: FileIntakeOptions) {
  const toast = useAppToast();
  const [drag, setDrag] = useState<IntakeDrag>(IDLE);
  const [result, setResult] = useState('');
  const announceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // The latest options, read at drop time: a drop can land after a re-render
  // changed the destination or the read-only state.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const reportOutcome = useIntakeReport();
  const report = useCallback(
    (outcome: IntakeResult, source: IntakeSource) => {
      const { maxFiles, overLimitHint } = optionsRef.current;
      reportOutcome(outcome, source, { maxFiles, overLimitHint });
    },
    [reportOutcome],
  );

  /**
   * Put an outcome in the live region. Cleared first and set a tick later, so
   * a second identical outcome (two picks of one file) is still a change a
   * screen reader announces.
   */
  const announce = useCallback((text: string) => {
    clearTimeout(announceTimer.current);
    setResult('');
    announceTimer.current = setTimeout(() => setResult(text), 0);
  }, []);

  /** Run files through the gate and hand over what passed. Every route ends here. */
  const addFiles = useCallback(
    (files: readonly File[], source: IntakeSource) => {
      const current = optionsRef.current;
      if (current.readOnlyReason) {
        toast({
          title: "You can't add files here",
          description: current.readOnlyReason,
          status: 'warning',
        });
        announce(`You can't add files here. ${current.readOnlyReason}`);
        return;
      }
      const named = source === 'paste' ? files.map((f, i) => nameClipboardFile(f, i)) : files;
      const outcome = validateFiles(named, current.rule, current);
      if (!current.selfReported) report(outcome, source);
      if (outcome.accepted.length === 0) {
        announce(`Couldn't add ${plural(outcome.rejected.length, 'file')}`);
        return;
      }

      const { accepted, skipped, rejected } = outcome;
      if (!current.selfReported) {
        safeAnalytics.track(ANALYTICS_EVENTS.FILE_INTAKE_ACCEPTED, {
          surface: current.surface,
          source,
          file_count_bucket: countBucket(accepted.length),
          skipped_count: skipped,
          rejected_count: rejected.length,
        });
      }
      // A self-reported surface may still refuse some, and says so itself.
      if (!current.selfReported) {
        const to = current.destinationLabel ? ` to ${current.destinationLabel}` : '';
        announce(`Adding ${plural(accepted.length, 'file')}${to}`);
      }
      current.onFiles(accepted.map(typeImageByName), {
        source,
        skipped,
        rejected: rejected.length,
      });
    },
    [announce, report, toast],
  );

  const dropzone = useDropzone({
    // A read-only place refuses drops visibly (below) and opens no picker.
    noClick: options.dropOnly || Boolean(options.readOnlyReason),
    noKeyboard: options.dropOnly || Boolean(options.readOnlyReason),
    noPaste: !options.paste,
    onDragEnter: (event: DragEvent<HTMLElement>) => {
      // A new drag replaces the last outcome; it must not be re-read when the drag ends.
      clearTimeout(announceTimer.current);
      setResult('');
      setDrag(classifyDrag(Array.from(event.dataTransfer?.items ?? []), optionsRef.current));
    },
    // No accept, size, count or single-file limit reaches react-dropzone (the
    // picker's `multiple` is set on the input only), so every file arrives here
    // as accepted and validateFiles is the only judge.
    onDrop: (files, _rejections, event) => {
      setDrag(IDLE);
      const native = event ? nativeEventOf(event) : null;
      if (optionsRef.current.skipHandledDrops && native && isDropHandled(native)) return;
      addFiles(files, sourceOf(event));
    },
  });

  useEffect(() => () => clearTimeout(announceTimer.current), []);

  const currentDrag = dropzone.isDragActive ? drag : IDLE;
  const { getRootProps, getInputProps } = dropzone;
  const { rule, maxFiles } = options;

  return {
    /** Spread on the area files land on; caller handlers (item drags) are merged in. */
    getRootProps: <T extends DropzoneRootProps>(extra?: T) => getRootProps(extra),
    getInputProps: () =>
      getInputProps({ accept: rule.inputAccept || undefined, multiple: maxFiles > 1 }),
    /** Open the picker. Call inside the user's click, or the browser blocks it. */
    open: dropzone.open,
    addFiles,
    drag: currentDrag,
    /** For a polite live region: the drag while there is one, else the last result. */
    liveText:
      currentDrag.state === 'idle'
        ? result
        : describeDrag(currentDrag, options.destinationLabel, Boolean(options.readOnlyReason)),
    rule,
    maxFiles,
    maxBytes: options.maxBytes,
    destinationLabel: options.destinationLabel,
    readOnlyReason: options.readOnlyReason,
    isFocused: dropzone.isFocused,
  };
}

export type FileIntake = ReturnType<typeof useFileIntake>;
